// AdminPickupListPanel — read-only inline panel admins see in the
// admin/Children edit sheet, listing authorised pickup people for a
// child. Useful when a parent calls and asks "who's on the list?"
//
// Admins can also remove (hard-delete) outdated entries.

import { useEffect, useState } from "react";
import { Phone, ShieldCheck, TrashSimple, UserList } from "@phosphor-icons/react";
import { toast } from "sonner";
import { listForChild, removeAuthorization } from "@/lib/pickupAuthorizations";
import PickupPhoto from "@/components/pickup/PickupPhoto";

export default function AdminPickupListPanel({ childId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!childId) return;
    setLoading(true);
    try {
      setRows(await listForChild(childId, { includeInactive: false }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const onRemove = async (row) => {
    if (
      !window.confirm(
        `Permanently remove ${row.full_name} from this child's pickup list?`,
      )
    )
      return;
    try {
      await removeAuthorization(row.id);
      toast.success("Removed");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not remove");
    }
  };

  if (loading) return null;

  return (
    <section
      data-testid="admin-pickup-panel"
      className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3"
    >
      <header className="mb-2 flex items-center gap-2">
        <UserList size={14} weight="duotone" className="text-primary-hover" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">
          Authorised pickup ({rows.length})
        </p>
      </header>
      {rows.length === 0 ? (
        <p
          data-testid="admin-pickup-empty"
          className="text-[12px] text-neutral-500"
        >
          Only legal guardians on file can collect this child.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`admin-pickup-row-${r.id}`}
              className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-white p-2"
            >
              <PickupPhoto path={r.photo_path} name={r.full_name} size={36} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-neutral-900">
                  {r.full_name}
                  {r.is_emergency_contact && (
                    <span className="ml-2 rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8A5A12]">
                      Emergency
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-neutral-600">
                  {r.relationship || "—"}
                  {r.phone ? ` · ${r.phone}` : ""}
                  {r.pin && (
                    <span className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[9px] font-semibold text-amber-800 align-middle">
                      <ShieldCheck size={8} weight="duotone" />
                      PIN
                    </span>
                  )}
                </p>
                {r.notes && (
                  <p className="text-[11px] italic text-neutral-500">
                    "{r.notes}"
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {r.phone && (
                  <a
                    href={`tel:${r.phone}`}
                    title="Call"
                    className="rounded-full border border-neutral-200 bg-white p-1 text-neutral-700 hover:bg-neutral-50"
                  >
                    <Phone size={12} weight="duotone" />
                  </a>
                )}
                <button
                  type="button"
                  data-testid={`admin-pickup-remove-${r.id}`}
                  onClick={() => onRemove(r)}
                  title="Remove"
                  className="rounded-full border border-neutral-200 bg-white p-1 text-[#8A5A12] hover:bg-[#C6851E]/5"
                >
                  <TrashSimple size={12} weight="bold" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
